import React from 'react';
import { graphql } from 'gatsby';
import { Helmet } from 'react-helmet';
import { getCurrentUrl } from '~utils/urls';

import PageTitleContext from '~contexts/PageTitle';
import { useLanguage } from '~contexts/Language';

import Layout from '~globals/Layout';
import PostHero from '~postSections/PostHero';
import PostContent from '~postSections/PostContent';

const PostResources = ({ data }) => {
	const { language } = useLanguage();
	const {
		author,
		publishDate,
		title,
		metaTitle,
		image,
		metaImage,
		disclaimer,
		summary,
		metaDescription,
		content,
		categories,
		adDisclosure,
		slug,
	} = data[language] ?? data.contentfulPostResources;
	const { defaultMetaDescription, defaultMetaImage } = data.contentfulGlobalSettings;

	const displayTitle = `${metaTitle?.trim() || title?.trim()} | Nova Credit`;
	const displayDescription = metaDescription || summary || defaultMetaDescription;
	const displayImage = metaImage ? metaImage.url : image ? image.url : defaultMetaImage.url;
	const url = getCurrentUrl('resources', slug);

	return (
		<PageTitleContext.Provider value={displayTitle}>
			<Layout parentGroup="individual">
				<Helmet>
					<title>{displayTitle}</title>
					<meta name="name" content={displayTitle} />
					<meta name="url" content={url} />
					<meta name="description" content={displayDescription} />

					<meta property="og:title" content={displayTitle} />
					<meta property="og:site_name" content="Nova Credit" />
					<meta property="og:type" content="website" />
					<meta property="og:url" content={url} />
					<meta property="og:image" content={displayImage} />
					<meta property="og:description" content={displayDescription} />

					<meta name="twitter:title" content={displayTitle} />
					<meta name="twitter:url" content={url} />
					<meta name="twitter:card" content="summary_large_image" />
					<meta name="twitter:description" content={displayDescription} />
					<meta name="twitter:image" content={displayImage} />

					<link rel="canonical" href={url} />
				</Helmet>
				<PostHero
					type="Resources"
					publishDate={publishDate}
					title={title}
					image={image}
					summary={summary}
					categories={categories}
					adDisclosure={adDisclosure}
				/>
				{content && <PostContent disclaimer={disclaimer} author={author} content={content} />}
			</Layout>
		</PageTitleContext.Provider>
	);
};

export const Head = () => {
	return (
		<>
			<link rel="preconnect" href="https://fonts.googleapis.com" />
			<link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
			<link
				href="https://fonts.googleapis.com/css2?family=Newsreader:opsz@6..72&display=swap"
				rel="stylesheet"
			></link>
		</>
	);
};

export const query = graphql`
	fragment postResourcesFields on ContentfulPostResources {
		publishDate(formatString: "MMMM Do YYYY")
		title
		metaTitle
		slug
		image {
			url
			description
		}
		metaImage {
			url
		}
		author {
			name
			photo {
				url
			}
			title
		}
		summary
		metaDescription
		disclaimer {
			text {
				raw
			}
		}
		content {
			raw
			references {
				__typename
				... on ContentfulBodyWidgetDefault {
					contentful_id
					eyebrow
					headline
					body {
						raw
					}
					widgetColor
					form
					cta {
						href
						label
						openInNewTab
					}
					companyLogo {
						url
					}
				}
				... on ContentfulAsset {
					id
					url
					contentful_id
					title
				}
			}
		}
		categories {
			name
			slug
		}
		adDisclosure
	}

	query PostResourcesQuery($contentful_id: String) {
		en: contentfulPostResources(
			contentful_id: { eq: $contentful_id }
			node_locale: { eq: "en-US" }
		) {
			...postResourcesFields
		}
		es: contentfulPostResources(contentful_id: { eq: $contentful_id }, node_locale: { eq: "es" }) {
			...postResourcesFields
		}
		fr: contentfulPostResources(
			contentful_id: { eq: $contentful_id }
			node_locale: { eq: "fr-CA" }
		) {
			...postResourcesFields
		}
		contentfulGlobalSettings {
			defaultMetaDescription
			defaultMetaImage {
				url
			}
		}
	}
`;

export default PostResources;
